import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Tracks an element's rendered width (screen px). Attach the returned ref; the width is read as the
 * element mounts — before paint, so a layout that depends on it never visibly jumps — and again
 * whenever it resizes (a `ResizeObserver`), e.g. when a web font finishes loading. Nothing is
 * re-measured on ordinary re-renders. Stays 0 until something is attached.
 */
export function useElementWidth<T extends HTMLElement>(): [(element: T | null) => void, number] {
  const [width, setWidth] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  const ref = useCallback((element: T | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!element) return

    setWidth(element.offsetWidth)

    if (typeof ResizeObserver === 'undefined') return

    observerRef.current = new ResizeObserver(() => setWidth(element.offsetWidth))
    observerRef.current.observe(element)
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return [ref, width]
}
